import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  getAuth,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithCredential,
  onAuthStateChanged,
  setPersistence,
  signOut,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  sendEmailVerification,
  reload,
  checkActionCode,
  applyActionCode,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const { isFirebaseConfigReady } = require("shared/firebaseConfiguration");
const isFirebaseConfigured = isFirebaseConfigReady(firebaseConfig);

const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null;
const auth = app ? getAuth(app) : null;
const googleProvider = new GoogleAuthProvider();
const pendingGoogleCredentialStorageKey = "firebase_pending_google_credential";

const requireAuth = () => {
  if (!auth)
    throw Object.assign(
      new Error("O acesso está temporariamente indisponível. Tente novamente em instantes."),
      { code: "auth/configuration-unavailable" }
    );
  return auth;
};
export const setAuthPersistence = async () =>
  setPersistence(requireAuth(), browserLocalPersistence);
export const signInWithEmailAndPasswordFirebase = async (email, password) =>
  signInWithEmailAndPassword(requireAuth(), email, password);
export const signUpWithEmailAndPasswordFirebase = async (name, email, password) => {
  const userCredential = await createUserWithEmailAndPassword(requireAuth(), email, password);
  if (name.trim()) await updateProfile(userCredential.user, { displayName: name.trim() });
  return userCredential;
};
export const signInWithGoogleFirebase = async () => signInWithPopup(requireAuth(), googleProvider);

export const linkGoogleToCurrentUserFirebase = () => {
  if (!auth?.currentUser) {
    return Promise.reject(new Error("Nenhum usuario autenticado para vincular Google."));
  }

  return linkWithPopup(auth.currentUser, googleProvider);
};

export const fetchSignInMethodsForEmailFirebase = async (email) =>
  fetchSignInMethodsForEmail(requireAuth(), email);

export const getGoogleCredentialFromError = (error) =>
  GoogleAuthProvider.credentialFromError(error);

export const storePendingGoogleCredential = (credential) => {
  if (!credential || typeof window === "undefined") return;

  window.sessionStorage.setItem(
    pendingGoogleCredentialStorageKey,
    JSON.stringify({
      idToken: credential.idToken || null,
      accessToken: credential.accessToken || null,
    })
  );
};

export const getPendingGoogleCredential = () => {
  if (typeof window === "undefined") return null;

  const rawCredential = window.sessionStorage.getItem(pendingGoogleCredentialStorageKey);

  if (!rawCredential) return null;

  try {
    const parsedCredential = JSON.parse(rawCredential);

    if (!parsedCredential?.idToken && !parsedCredential?.accessToken) {
      return null;
    }

    return GoogleAuthProvider.credential(
      parsedCredential.idToken || null,
      parsedCredential.accessToken || null
    );
  } catch (error) {
    return null;
  }
};

export const clearPendingGoogleCredential = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(pendingGoogleCredentialStorageKey);
};

export const linkPendingGoogleCredentialToUser = async (user) => {
  const pendingCredential = getPendingGoogleCredential();

  if (!auth || !user || !pendingCredential) {
    return null;
  }

  const linkedUser = await linkWithCredential(user, pendingCredential);
  clearPendingGoogleCredential();
  return linkedUser;
};

export const linkPasswordToCurrentUserFirebase = (email, password) => {
  if (!auth?.currentUser) {
    return Promise.reject(new Error("Nenhum usuario autenticado para vincular senha."));
  }

  const credential = EmailAuthProvider.credential(email, password);
  return linkWithCredential(auth.currentUser, credential);
};

export const signOutFirebase = () => (auth ? signOut(auth) : Promise.resolve());

export const subscribeToAuthChanges = (callback) => {
  if (!auth) {
    callback(null);
    return () => {};
  }

  return onAuthStateChanged(auth, callback);
};

export { auth };

const verificationRequests = new Map();
const verificationKey = (uid) => `pso:verification-email:${uid}`;
export const lastVerificationEmailAt = (uid) => {
  try {
    return Number(window.sessionStorage.getItem(verificationKey(uid)) || 0);
  } catch {
    return 0;
  }
};
export const sendAccountVerification = async () => {
  const instance = requireAuth(),
    user = instance.currentUser;
  if (!user) throw new Error("Entre na sua conta para verificar o e-mail.");
  if (user.emailVerified) return;
  if (verificationRequests.has(user.uid)) return verificationRequests.get(user.uid);
  if (Date.now() - lastVerificationEmailAt(user.uid) < 60000)
    throw new Error("Aguarde um minuto antes de solicitar outro e-mail.");
  instance.languageCode = "pt-BR";
  const operation = sendEmailVerification(user, {
    url: `${window.location.origin}/verificar-conta`,
    handleCodeInApp: false,
  })
    .then(() => {
      try {
        window.sessionStorage.setItem(verificationKey(user.uid), String(Date.now()));
      } catch {}
    })
    .finally(() => verificationRequests.delete(user.uid));
  verificationRequests.set(user.uid, operation);
  return operation;
};
export const refreshAccountVerification = async () => {
  const user = requireAuth().currentUser;
  if (!user) return false;
  await reload(user);
  await user.getIdToken(true);
  if (user.emailVerified) window.dispatchEvent(new CustomEvent("pso:email-verified"));
  return user.emailVerified;
};
const verificationActions = new Map();
export const confirmAccountVerification = (code) => {
  if (!code) return Promise.reject(new Error("Este link de verificação está incompleto."));
  if (verificationActions.has(code)) return verificationActions.get(code);
  const operation = (async () => {
    const instance = requireAuth();
    const action = await checkActionCode(instance, code);
    if (action.operation !== "VERIFY_EMAIL")
      throw new Error("Este link não é de verificação de e-mail.");
    await applyActionCode(instance, code);
    if (instance.currentUser?.email?.toLowerCase() === action.data.email?.toLowerCase())
      await refreshAccountVerification();
    return action.data.email;
  })();
  verificationActions.set(code, operation);
  operation.catch(() => verificationActions.delete(code));
  return operation;
};

export const redirectToDefaultEmailAction = (query) => {
  const { defaultEmailActionUrl } = require("shared/firebaseEmailActions");
  const url = defaultEmailActionUrl(query, {
    projectId: firebaseConfig.projectId,
    apiKey: firebaseConfig.apiKey,
    origin: window.location.origin,
  });
  window.location.replace(url);
};
